// Bildschirm "Kontaktwege" -- die Pflege (ST-PTY-002, erster Schnitt).
//
// WARUM ER EIN EIGENER BILDSCHIRM IST UND NICHT EIN MODUS DES KONTAKTDETAILS.
// Kapitel 5.2 Nr. 2 verlangt EINE Hauptaktion je Bildschirm. Das Detail hat
// seine -- "diesen Kontakt oeffnen und lesen" --, und ein Bearbeitungsmodus
// darin haette daneben eine zweite gesetzt. Ein eigener Bildschirm unter
// `/kontakte/{partyId}/kontaktwege` hat eine eigene Adresse, einen eigenen
// Titel und genau eine Hauptaktion: einen Kontaktweg hinzufuegen.
//
// PROGRESSIVE OFFENLEGUNG (NFR-UX-004, Kapitel 5.2 Nr. 9) an drei Stellen:
//   * Der Endgrund erscheint ERST, wenn jemand "Beenden" waehlt. Eine
//     Gruendeauswahl an jeder Zeile waere eine Maske aus Auswahlfeldern fuer
//     eine Handlung, die selten vorkommt.
//   * Die frueheren Kontaktwege liegen in einer zugeklappten Sicht. Sie sind
//     der Beleg dafuer, dass beendet und nicht geloescht wurde -- aber sie
//     sind nicht die Arbeit.
//   * Kennung und Herkunft erscheinen gar nicht. Wer anruft, sucht die Nummer.
//
// SICHERE FEHLERERHOLUNG (NFR-UX-005, CM-UX-004): jede Stoerung nennt Ursache,
// naechsten Schritt und -- nur dann -- einen Wiederholweg. Der KONFLIKT ist
// der Fall, an dem sich das entscheidet: er bekommt ausdruecklich KEIN
// "erneut versuchen", weil ein zweiter Versuch mit demselben veralteten Stand
// genauso endet. Er bekommt "neu laden", und der Knopf tut genau das.
//
// "ABSENDEN FEHLGESCHLAGEN" (ADR-0060): reisst beim Hinzufuegen die
// Verbindung ab, bietet der Bildschirm "Erneut versuchen" an. Das ist erst
// sicher, seit jedes Absenden eine Kennung traegt und die Wiederholung
// DIESELBE -- der Server antwortet dann mit dem Original statt einer zweiten
// Zeile (client-request.ts).
//
// BARRIEREFREIHEIT (NFR-ACC-001): GENAU EINE Region mit `aria-live` je
// Bildschirm; der Fokus wandert nach jedem Befehl dorthin, damit niemand die
// Antwort suchen muss. Zustaende stehen in Worten und nicht in einer Farbe
// allein. Jeder Zeilenknopf traegt den Wert in seinem Namen -- "Beenden"
// allein waere in einer Liste von vier Knoepfen derselbe Name viermal.
//
// DIE OBERFLAECHE ENTSCHEIDET NICHTS. Sichtbarkeit, Sperrstand und Endgrund
// entscheidet der Server; dieser Bildschirm schickt und uebersetzt.

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Badge, Body1, Button, Caption1, Field, Input, MessageBar, MessageBarBody, MessageBarTitle, Radio, RadioGroup } from '@fluentui/react-components';

import {
  addContactPoint,
  addContactPointUsage,
  channelLabel,
  groupContactPoints,
  confirmationFor,
  correctContactPoint,
  correctionConfirmationWith,
  correctionSuppressionNote,
  correctionSuppressionWarning,
  CORRECTION_NOT_SAVED_MESSAGE,
  CORRECTION_OPEN_MESSAGE,
  endContactPoint,
  endReasonLabel,
  HISTORY_TRUNCATED_NOTICE,
  OFFERED_CONTACT_POINT_USAGES,
  OFFERED_END_REASONS,
  WEBSITE_END_REASONS,
  preferContactPoint,
  SUBMIT_FAILED_MESSAGE,
  USAGE_SUBMIT_FAILED_MESSAGE,
  usageConfirmation,
  WEBSITE_NICHT_ERSETZBAR,
  type CommandResult,
  type ContactPointGroup,
  type CorrectionResult,
  type CorrectionSuppressionOutcome,
  type ListedChannel,
  type ManagedChannel,
} from '../contact-points.ts';
import {
  fetchParty,
  formatInstant,
  partyHref,
  usageLabel,
  verificationLabel,
  bestaetigungPilleSichtbar,
  type ContactPointView,
  type EndedContactPointView,
  type PartyDetail,
} from '../parties.ts';
import { validationMessageFor } from '../quick-capture.ts';
import { notWritableNotice } from '../party-lifecycle.ts';
import { Pflegeabschnitt, Pflegehistorie, Pflegeliste, usePflegeStyles, Zeilenaktionen } from '../shell/pflegeabschnitt.tsx';
import { AnlegenFormular, AnlegenOeffner, PflegeKopf, useAnlegen } from './pflege-kopf.tsx';
import { useScreenStyles } from '../shell/screen-styles.ts';
import { useMaintainedSection } from '../shell/use-maintained-section.ts';
import { WeitereVerwendung } from './weitere-verwendung.tsx';
import { KontaktwegKorrigieren } from './kontaktweg-korrigieren.tsx';
import { useSperren } from './kontakt/sperren-abschnitt.tsx';
import { endSuppression, fetchSuppressions, liftOffered, reasonWord, suppressionsFor, type SuppressionView } from '../communication-suppressions.ts';
import { AppLink } from '../shell/router.tsx';

export interface KontaktwegeScreenProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly partyId: string;
}

/**
 * Ein gueltiger Kontaktweg mit seinen Zwecken. Beendet und bevorzugt wird JE
 * ZWECK: "die Rechnung geht nicht mehr an diese Adresse" beendet nicht den
 * geschaeftlichen Weg. Der Endgrund ist Pflicht und erscheint erst mit der
 * Beendigung: ohne ihn sind "umgezogen", "nicht mehr erreichbar" und "Zweck
 * entfallen" spaeter ununterscheidbar (ADR-0022).
 */
function Kontaktweg({
  gruppe,
  beendenFuer,
  verwendenOffen,
  grund,
  neuerZweck,
  busy,
  zuruecktreten,
  nurLesen,
  nurBeenden,
  onBeendenOeffnen,
  onBeendenAbbrechen,
  onGrund,
  onBeenden,
  onBevorzugen,
  onNeuerZweck,
  onVerwendenOeffnen,
  onVerwendenAbbrechen,
  onVerwenden,
  korrektur,
}: {
  readonly gruppe: ContactPointGroup<ContactPointView & { readonly label: string }>;
  readonly beendenFuer: string | undefined;
  readonly verwendenOffen: boolean;
  readonly grund: string;
  readonly neuerZweck: string;
  readonly busy: boolean;
  readonly zuruecktreten: boolean;
  /** Der Kontakt ist nicht beschreibbar: kein "Auch verwenden als". */
  readonly nurLesen: boolean;
  /**
   * Die Website: nur "Nicht mehr verwenden". Ein weiterer Zweck waere ein
   * Anlegen ohne Formregel, und "bevorzugt" wertet fuer sie keine Ansicht aus
   * -- Knopf und Pille waeren ein Zustand ohne Wirkung (DESIGN zu #780).
   */
  readonly nurBeenden: boolean;
  readonly onBeendenOeffnen: (contact: ContactPointView) => void;
  readonly onBeendenAbbrechen: () => void;
  readonly onGrund: (code: string) => void;
  readonly onBeenden: (contact: ContactPointView) => void;
  readonly onBevorzugen: (contact: ContactPointView) => void;
  readonly onNeuerZweck: (code: string) => void;
  readonly onVerwendenOeffnen: () => void;
  readonly onVerwendenAbbrechen: () => void;
  readonly onVerwenden: () => void;
  /** "E-Mail korrigieren": einmal je Kontaktweg, nicht je Zweck; fehlt am nicht beschreibbaren Kontakt. */
  readonly korrektur?: ReactElement | undefined;
}): ReactElement {
  const own = usePflegeStyles();
  const mehrere = gruppe.usages.length > 1;
  const vorhanden = new Set(gruppe.usages.map((usage) => usage.usageCode));
  const weitere = OFFERED_CONTACT_POINT_USAGES.filter((usage) => !vorhanden.has(usage.code));
  const einziger = gruppe.usages[0];
  return (
    <li className={own.eintrag}>
      <Body1 className={own.wert}>{gruppe.value}</Body1>
      <span className={own.merkmale}>
        <Caption1>
          {gruppe.first.label},{' '}
          {mehrere ? `Zwecke: ${gruppe.usages.map((usage) => usageLabel(usage.usageCode)).join(', ')}` : usageLabel(einziger?.usageCode ?? '')}
        </Caption1>
        {!mehrere && !nurBeenden && einziger?.isPreferred === true ? (
          // In WORTEN und nicht als Stern (NFR-ACC-001, WCAG 1.4.1).
          <Badge appearance="outline" color="brand">
            bevorzugt
          </Badge>
        ) : undefined}
        {bestaetigungPilleSichtbar(gruppe.verificationStatus) ? (
          <Badge appearance="outline" color="informative">
            {verificationLabel(gruppe.verificationStatus)}
          </Badge>
        ) : undefined}
      </span>

      {/* Zuerst, was ergaenzt oder korrigiert; "Beenden" (aendert den Bestand) steht am Ende
          der Reihe, damit der riskante Knopf nie zuerst unter dem Finger liegt. */}
      {nurLesen ? undefined : korrektur}
      {!nurLesen && !nurBeenden && weitere.length > 0 ? (
        <WeitereVerwendung
          name={gruppe.value}
          frage="Wofür soll dieser Kontaktweg zusätzlich gelten?"
          wirkung="Es entsteht kein zweiter Kontaktweg. Die neue Verwendung ist nicht bevorzugt, und jeder Zweck lässt sich später einzeln wieder aufgeben."
          optionen={weitere}
          offen={verwendenOffen}
          busy={busy}
          wert={neuerZweck}
          onWert={onNeuerZweck}
          onOeffnen={onVerwendenOeffnen}
          onAbbrechen={onVerwendenAbbrechen}
          onBestaetigen={onVerwenden}
          zuruecktreten={zuruecktreten}
        />
      ) : undefined}

      {gruppe.usages.map((contact) => {
        const zweck = usageLabel(contact.usageCode);
        // Der WERT steht im Namen der Knoepfe, bei mehreren Zwecken dazu der
        // Zweck: in einer Liste hiessen sie sonst alle gleich.
        const name = mehrere ? `${zweck} ${gruppe.value}` : gruppe.value;
        return (
          <div key={contact.contactPointUsageId} className={mehrere ? undefined : own.zweckEinzeln}>
            {mehrere ? (
              <Caption1 className={own.hinweis}>
                {zweck}
                {contact.isPreferred && !nurBeenden ? ', bevorzugt' : ''}
              </Caption1>
            ) : undefined}
            <Zeilenaktionen
              name={name}
              offen={beendenFuer === contact.contactPointUsageId}
              busy={busy}
              aktion="Nicht mehr verwenden"
              frage={mehrere ? `Warum gilt der Zweck „${zweck}“ an diesem Kontaktweg nicht mehr?` : 'Warum wird dieser Kontaktweg nicht mehr verwendet?'}
              wirkung={
                mehrere
                  ? 'Nur dieser Zweck fällt weg. Die übrigen gelten weiter; er bleibt unter „Frühere Kontaktwege“ nachlesbar.'
                  : 'Der Kontaktweg wird nicht gelöscht. Er bleibt unter „Frühere Kontaktwege“ nachlesbar.'
              }
              bestaetigen={mehrere ? 'Für diesen Zweck nicht mehr verwenden' : 'Nicht mehr verwenden'}
              gruende={{ optionen: nurBeenden ? WEBSITE_END_REASONS : OFFERED_END_REASONS, wert: grund, onWert: onGrund, id: `grund-${contact.contactPointUsageId}` }}
              onOeffnen={() => onBeendenOeffnen(contact)}
              onAbbrechen={onBeendenAbbrechen}
              onBeenden={() => onBeenden(contact)}
            >
              {contact.isPreferred || nurLesen || nurBeenden ? undefined : (
                <Button
                  appearance="secondary"
                  size="small"
                  className={own.zeilenknopf}
                  disabled={busy}
                  aria-label={`Als bevorzugt festlegen: ${name}`}
                  onClick={() => onBevorzugen(contact)}
                >
                  Als bevorzugt festlegen
                </Button>
              )}
            </Zeilenaktionen>
          </div>
        );
      })}

    </li>
  );
}

function FruehererKontaktweg({
  entry,
  label,
}: {
  readonly entry: EndedContactPointView;
  readonly label: string;
}): ReactElement {
  const own = usePflegeStyles();
  return (
    <li className={own.eintrag}>
      <Body1 className={own.wert}>{entry.value}</Body1>
      <span className={own.merkmale}>
        <Caption1>
          {label}, {usageLabel(entry.usageCode)}
        </Caption1>
        <Caption1>nicht mehr verwendet seit {formatInstant(entry.endedAt)}</Caption1>
        <Badge appearance="outline" color="informative">
          {endReasonLabel(entry.endReasonCode)}
        </Badge>
      </span>
    </li>
  );
}

export function KontaktwegeScreen({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  partyId,
}: KontaktwegeScreenProps): ReactElement {
  const styles = useScreenStyles();
  const own = usePflegeStyles();
  const [kanal, setKanal] = useState<ManagedChannel>('email');
  const [wert, setWert] = useState('');
  const [beendenFuer, setBeendenFuer] = useState<string | undefined>(undefined);
  const [grund, setGrund] = useState<string>(OFFERED_END_REASONS[0]?.code ?? '');
  /** Der Kontaktweg, dessen Zweckwahl offen ist, und der gewaehlte Zweck -- ohne Vorbelegung. */
  const [verwendenFuer, setVerwendenFuer] = useState<string | undefined>(undefined);
  const [neuerZweck, setNeuerZweck] = useState('');
  /** Der Kontaktweg, dessen Korrektur offen ist. */
  const [korrigierenFuer, setKorrigierenFuer] = useState<string | undefined>(undefined);
  /** Korrektur gespeichert, die Sperre aber nicht aufgehoben oder offen: eine Warnleiste mit Weg zu "Sperren". */
  const [sperrWarnung, setSperrWarnung] = useState<ReturnType<typeof correctionSuppressionWarning> | undefined>(undefined);
  const sperren = useSperren({ apiBaseUrl, accessToken, onReauthenticate, partyId });
  /**
   * Welches Anlegen "Erneut versuchen" wiederholt. Nur die ART steht hier;
   * wiederholt wird mit der Eingabe, die JETZT dasteht (dasselbe Muster wie
   * auf der Seite "Anschriften").
   */
  const letzteAnlage = useRef<
    { readonly art: 'kontaktweg' } | { readonly art: 'zweck'; readonly contactPointId: string; readonly usageCode: string }
  >({ art: 'kontaktweg' });

  const abschnitt = useMaintainedSection<PartyDetail>({
    accessToken,
    onReauthenticate,
    sectionKey: `${apiBaseUrl}|${partyId}`,
    fetchSection: async (token) => {
      const result = await fetchParty({ baseUrl: apiBaseUrl, accessToken: token, partyId });
      return result.outcome === 'loaded' ? { outcome: 'loaded', data: result.party } : result;
    },
    createMessages: { submitFailed: SUBMIT_FAILED_MESSAGE },
  });
  const { busy, fieldErrors: feldfehler, submitFailed: absendenFehlgeschlagen } = abschnitt;
  // Der naechste Befehl ersetzt die Warnleiste, wie er Meldung und Bestaetigung ersetzt.
  useEffect(() => {
    if (busy) setSperrWarnung(undefined);
  }, [busy]);
  const anlegen = useAnlegen(
    abschnitt.status === 'geladen' && abschnitt.data !== undefined && abschnitt.data.emailAddresses.length + abschnitt.data.phoneNumbers.length + abschnitt.data.websites.length === 0,
    partyId,
    abschnitt.status === 'laedt',
  );

  const befehl = (operation: 'end' | 'prefer', run: (token: string) => Promise<CommandResult>): void => {
    void abschnitt.command({
      run,
      confirm: (applied) => confirmationFor(operation, applied.channel),
      onApplied: () => setBeendenFuer(undefined),
    });
  };

  /**
   * Hinzufuegen -- aus dem Formular UND aus "Erneut versuchen". Beide senden
   * die AKTUELLE Eingabe; ist sie inhaltlich dieselbe wie beim letzten
   * Absenden, reist dieselbe Kennung mit, sonst eine neue.
   */
  const hinzufuegen = (): void => {
    letzteAnlage.current = { art: 'kontaktweg' };
    void abschnitt.create({
      input: { channel: kanal, value: wert },
      run: (token, clientRequestId) =>
        addContactPoint({ baseUrl: apiBaseUrl, accessToken: token, partyId, channel: kanal, value: wert, clientRequestId }),
      confirm: (applied) => confirmationFor('add', applied.channel),
      clearInput: () => setWert(''),
      onApplied: () => {
        setBeendenFuer(undefined);
        anlegen.erledigt();
      },
    });
  };

  const verwendenSchliessen = (): void => {
    setVerwendenFuer(undefined);
    setNeuerZweck('');
  };

  /**
   * Kontaktweg korrigieren. Bleibt die Antwort aus (Netz, 502/504), laedt der
   * Bildschirm den Kontakt still nach und vergleicht: steht die Eingabe schon
   * da, ist es ein Erfolg; steht der alte Wert da, ist nichts gespeichert;
   * scheitert auch das Nachladen, ist es offen.
   */
  const korrigieren = (
    gruppe: ContactPointGroup<ContactPointView & { readonly channel: ListedChannel }>,
    eingabe: string,
    /** Jede Sperre "Nicht zustellbar" an genau diesem Weg (gruppenweit und je Haus koennen nebeneinander stehen). */
    unzustellbar: readonly SuppressionView[],
    aufheben: boolean,
  ): void => {
    const quelle = gruppe.usages[0];
    if (quelle === undefined) {
      return;
    }
    const neu = eingabe.trim();
    let sperrFolge: CorrectionSuppressionOutcome = unzustellbar.length === 0 ? 'keine' : 'gilt-weiter';
    /** Der jetzige Wert des Wegs, oder `undefined`, wenn das Nachladen scheitert. */
    const jetzigerStand = async (token: string): Promise<ContactPointView | null | undefined> => {
      const stand = await fetchParty({ baseUrl: apiBaseUrl, accessToken: token, partyId }).catch(() => undefined);
      if (stand?.outcome !== 'loaded') return undefined;
      return [...stand.party.emailAddresses, ...stand.party.phoneNumbers].find(
        (eintrag) => eintrag.contactPointUsageId === quelle.contactPointUsageId,
      ) ?? null;
    };
    const korrekturSenden = async (token: string): Promise<CorrectionResult> => {
      let ergebnis: CorrectionResult;
      try {
        ergebnis = await correctContactPoint({
          baseUrl: apiBaseUrl,
          accessToken: token,
          partyId,
          contactPointUsageId: quelle.contactPointUsageId,
          value: neu,
          contactPointLockVersion: quelle.contactPointLockVersion,
        });
      } catch (fehler) {
        const jetzt = await jetzigerStand(token);
        if (jetzt === undefined) throw fehler;
        if (jetzt?.value === neu) {
          return { outcome: 'applied', channel: gruppe.first.channel, changed: true, contactPointLockVersion: jetzt.contactPointLockVersion };
        }
        if (jetzt?.value === gruppe.value) {
          return { outcome: 'rejected', message: CORRECTION_NOT_SAVED_MESSAGE };
        }
        throw fehler;
      }
      // "Inzwischen geändert", und es gilt schon die Eingabe (etwa die eigene Korrektur vor
      // einem Abbruch): ein Erfolg, kein Konfliktsatz.
      if (ergebnis.outcome === 'conflict' && ergebnis.reasonCode === 'contact_point_outdated') {
        const jetzt = await jetzigerStand(token);
        if (jetzt?.value === neu) {
          return { outcome: 'applied', channel: gruppe.first.channel, changed: true, contactPointLockVersion: jetzt.contactPointLockVersion };
        }
      }
      return ergebnis;
    };
    void abschnitt.command({
      run: async (token): Promise<CorrectionResult> => {
        const korrektur = await korrekturSenden(token);
        // Zweiter Schritt, nicht atomar: nur nach gespeicherter Korrektur und gesetztem Haekchen.
        if (korrektur.outcome === 'applied' && unzustellbar.length > 0 && aufheben) {
          sperrFolge = await sperrenAufheben(token, unzustellbar);
        }
        // Hier hilft keine zweite Eingabe: das Formular schliesst, der Satz nennt den Ausweg.
        if (korrektur.outcome === 'conflict' && korrektur.reasonCode === 'contact_point_not_correctable') setKorrigierenFuer(undefined);
        return korrektur;
      },
      confirm: () => correctionConfirmationWith(gruppe.first.channel, neu, gruppe.usages.length, sperrFolge),
      onApplied: () => {
        setKorrigierenFuer(undefined);
        if (sperrFolge === 'nicht-aufgehoben' || sperrFolge === 'offen') {
          // Eine Leiste statt Erfolg plus Warnung: die Warnung nennt die Korrektur selbst als gespeichert.
          abschnitt.clearOutcome();
          setSperrWarnung(correctionSuppressionWarning(gruppe.first.channel, neu, sperrFolge));
        }
        if (unzustellbar.length > 0) void sperren.laden();
      },
      unreachable: CORRECTION_OPEN_MESSAGE,
    });
  };

  /**
   * Hebt jede "Nicht zustellbar" am Weg mit "Angabe korrigiert" ueber den
   * bestehenden Weg auf. "Aufgehoben" sagt die Bestaetigung nur, wenn keine
   * mehr gilt: bleibt eine, ist es der Teilerfolg; ist eine offen, offen.
   */
  const sperrenAufheben = async (token: string, liste: readonly SuppressionView[]): Promise<CorrectionSuppressionOutcome> => {
    const ausgaenge: CorrectionSuppressionOutcome[] = [];
    for (const sperre of liste) ausgaenge.push(liftOffered(sperre) ? await sperreAufheben(token, sperre) : 'nicht-aufgehoben');
    if (ausgaenge.includes('nicht-aufgehoben')) return 'nicht-aufgehoben';
    if (ausgaenge.includes('offen')) return 'offen';
    return 'aufgehoben';
  };

  /**
   * Eine Sperre aufheben. "Schon aufgehoben" zaehlt als aufgehoben: gewollt ist
   * der Zustand, nicht der eigene Schreibvorgang. Ein 404 belegt das nicht (der
   * Weg kann inzwischen unsichtbar sein): dann, wie ohne Antwort, mit den
   * Sperren abgleichen.
   */
  const sperreAufheben = async (token: string, sperre: SuppressionView): Promise<CorrectionSuppressionOutcome> => {
    const abgleichen = async (): Promise<CorrectionSuppressionOutcome> => {
      const stand = await fetchSuppressions({ baseUrl: apiBaseUrl, accessToken: token, partyId }).catch(() => undefined);
      if (stand?.outcome !== 'loaded') return 'offen';
      return stand.data.suppressions.some((eintrag) => eintrag.suppressionId === sperre.suppressionId) ? 'nicht-aufgehoben' : 'aufgehoben';
    };
    try {
      const ergebnis = await endSuppression({
        baseUrl: apiBaseUrl,
        accessToken: token,
        partyId,
        suppressionId: sperre.suppressionId,
        endReasonCode: 'contact_corrected',
        lockVersion: sperre.lockVersion ?? 0,
      });
      if (ergebnis.outcome === 'applied') return 'aufgehoben';
      if (ergebnis.outcome === 'conflict' && ergebnis.reasonCode === 'communication_suppression_already_ended') return 'aufgehoben';
      if (ergebnis.outcome === 'not-found') return await abgleichen();
      return 'nicht-aufgehoben';
    } catch {
      return await abgleichen();
    }
  };

  const verwenden = (gruppe: ContactPointGroup<ContactPointView>, usageCode: string): void => {
    const quelle = gruppe.usages[0];
    if (quelle === undefined || usageCode === '') {
      return;
    }
    letzteAnlage.current = { art: 'zweck', contactPointId: gruppe.contactPointId, usageCode };
    void abschnitt.create({
      input: { quelle: quelle.contactPointUsageId, usageCode },
      run: (token, clientRequestId) =>
        addContactPointUsage({
          baseUrl: apiBaseUrl,
          accessToken: token,
          partyId,
          sourceContactPointUsageId: quelle.contactPointUsageId,
          usageCode,
          clientRequestId,
        }),
      confirm: (applied) => usageConfirmation(applied.channel, usageCode),
      clearInput: verwendenSchliessen,
      messages: { submitFailed: USAGE_SUBMIT_FAILED_MESSAGE },
    });
  };

  const alleGruppen = (
    party: PartyDetail | undefined,
  ): readonly ContactPointGroup<ContactPointView & { readonly label: string; readonly channel: ListedChannel }>[] =>
    party === undefined
      ? []
      : groupContactPoints([
      ...party.emailAddresses.map((eintrag) => ({ ...eintrag, label: channelLabel('email'), channel: 'email' as const })),
      ...party.phoneNumbers.map((eintrag) => ({ ...eintrag, label: channelLabel('phone'), channel: 'phone' as const })),
      ...party.websites.map((eintrag) => ({ ...eintrag, label: channelLabel('web'), channel: 'web' as const })),
    ]);

  // Solange eine Korrektur offen ist, gehoeren die Feldmeldungen ihr und nicht dem Formular oben.
  const wertFehler = korrigierenFuer === undefined ? feldfehler.find((fehler) => fehler.fieldPath === 'value') : undefined;

  return (
    <>
    <PflegeKopf partyId={partyId} name={abschnitt.data?.displayName} bereich="Kontaktwege" />
    <Pflegeabschnitt
      abschnitt={abschnitt}
      partyId={partyId}
      texte={{
        laden: 'Kontaktwege werden geladen',
        feldfehlerTitel: 'Der Kontaktweg konnte nicht übernommen werden.',
        feldfehlerText: 'Prüfe die markierte Angabe und versuche es erneut.',
        neuLaden: 'Kontaktwege neu laden',
      }}
      onErneutAnlegen={() => {
        const letzte = letzteAnlage.current;
        if (letzte.art === 'kontaktweg') {
          // Steht das Formular zu (abgebrochen), oeffnet "Erneut versuchen" es zuerst:
          // die verworfene Eingabe geht nicht ungesehen hinaus.
          if (anlegen.offen) hinzufuegen();
          else anlegen.oeffnen();
          return;
        }
        // Nie das Formular senden, wenn ein Zweck gemeint war.
        const gruppe = alleGruppen(abschnitt.data).find(
          (eintrag) => eintrag.contactPointId === letzte.contactPointId,
        );
        if (gruppe === undefined) {
          abschnitt.reload();
          return;
        }
        verwenden(gruppe, verwendenFuer === gruppe.contactPointId && neuerZweck !== '' ? neuerZweck : letzte.usageCode);
      }}
      zusatzmeldung={
        // Dieselbe Region, keine zweite (NFR-ACC-001).
        sperrWarnung === undefined ? undefined : (
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>{sperrWarnung.titel}</MessageBarTitle>
              <div>{sperrWarnung.erklaerung}</div>
              <div>
                {sperrWarnung.vorLink}„<AppLink to={`${partyHref(partyId)}#sperren`}>Sperren</AppLink>“{sperrWarnung.nachLink}
              </div>
            </MessageBarBody>
          </MessageBar>
        )
      }
    >
      {(party) => {
        const gruppen = alleGruppen(party);
        // Einmal unter der Liste, solange es eine Website gibt oder gab: ersetzen laesst sie sich
        // hier nicht. Gerade nach dem Beenden der letzten steht sonst das Formular fuer E-Mail und
        // Telefon ohne Erklaerung da (DESIGN B13).
        const websiteHinweis = party.websites.length + party.endedWebsites.length > 0;
        const nurLesen = notWritableNotice(party.lifecycleStatus);
        const frueher: readonly (EndedContactPointView & { readonly label: string })[] = [
          ...party.endedEmailAddresses.map((eintrag) => ({ ...eintrag, label: channelLabel('email') })),
          ...party.endedPhoneNumbers.map((eintrag) => ({ ...eintrag, label: channelLabel('phone') })),
          ...party.endedWebsites.map((eintrag) => ({ ...eintrag, label: channelLabel('web') })),
        ];
        return (
          <>
            {nurLesen !== undefined ? (
              <MessageBar intent="info">
                <MessageBarBody>
                  <strong>{nurLesen.headline}</strong> {nurLesen.explanation}
                </MessageBarBody>
              </MessageBar>
            ) : undefined}

            <Pflegeliste
              id="kw-gueltig"
              titel="Aktuelle Kontaktwege"
              leer="Für diesen Kontakt ist bisher kein Kontaktweg hinterlegt, den du sehen darfst."
              anzahl={gruppen.length}
              // In der Region der Liste, nicht danach: wer ueber Landmarken springt, hoert ihn dort (B14).
              hinweis={websiteHinweis ? WEBSITE_NICHT_ERSETZBAR : undefined}
              aktion={nurLesen === undefined ? <AnlegenOeffner
                    steuerung={anlegen}
                    label="Kontaktweg hinzufügen"
                    disabled={busy}
                    zuruecktreten={
                      absendenFehlgeschlagen
                      || abschnitt.retryCommand !== undefined
                      || beendenFuer !== undefined
                      || verwendenFuer !== undefined
                      || korrigierenFuer !== undefined
                    }
                  /> : undefined}
              vorListe={
                nurLesen === undefined ? (
                  <AnlegenFormular
                    steuerung={anlegen}
                    name="Kontaktweg hinzufügen"
                    className={styles.form}
                    onSenden={hinzufuegen}
                    absenden={
                      // Waehrend "Erneut versuchen" steht, ist DAS die Hauptaktion;
                      // dieser Knopf tritt zurueck, damit es genau eine bleibt.
                      <Button appearance={absendenFehlgeschlagen ? 'secondary' : 'primary'} type="submit" disabled={busy || wert.trim().length === 0}>
                        {busy ? 'Wird gespeichert …' : 'Kontaktweg hinzufügen'}
                      </Button>
                    }
                  >
                    <Field label="Art des Kontaktwegs" required>
                      <RadioGroup
                        id="kw-art"
                        layout="horizontal"
                        value={kanal}
                        onChange={(_event, data) => setKanal(data.value as ManagedChannel)}
                      >
                        <Radio value="email" label="E-Mail" />
                        <Radio value="phone" label="Telefon" />
                      </RadioGroup>
                    </Field>
                    <Field
                      label={kanal === 'email' ? 'E-Mail-Adresse' : 'Telefonnummer'}
                      required
                      validationMessage={
                        wertFehler === undefined ? null : validationMessageFor(kanal === 'email' ? 'email' : 'phone', wertFehler.reasonCode).hint
                      }
                    >
                      <Input
                        id="kw-wert"
                        className={own.feld}
                        type={kanal === 'email' ? 'email' : 'tel'}
                        value={wert}
                        autoComplete={kanal === 'email' ? 'email' : 'tel'}
                        onChange={(_event, data) => setWert(data.value)}
                      />
                    </Field>
                  </AnlegenFormular>
                ) : undefined
              }
            >
              {gruppen.map((gruppe) => {
                // Sperren am Weg (auch kanalweite); das Haekchen nur, wo der Abschnitt Sperren aufheben wuerde.
                // An eine Website wird nichts versandt, also gibt es an ihr keine Sperre.
                const amWeg = gruppe.first.channel !== 'web' && sperren.status === 'geladen' && sperren.data !== undefined
                  ? suppressionsFor(sperren.data.suppressions, gruppe.first.channel, gruppe.contactPointId)
                  : [];
                const unzustellbar = amWeg.filter((sperre) => sperre.reasonCode === 'hard_bounce' && sperre.contactPointId === gruppe.contactPointId);
                const haekchen = unzustellbar.some(liftOffered);
                const sperrHinweise = [
                  ...new Set(amWeg.map((sperre) => correctionSuppressionNote(reasonWord(sperre.reasonCode), haekchen && unzustellbar.includes(sperre)))),
                ];
                return (
                <Kontaktweg
                  key={gruppe.contactPointId}
                  gruppe={gruppe}
                  busy={busy}
                  zuruecktreten={absendenFehlgeschlagen}
                  nurLesen={nurLesen !== undefined}
                  nurBeenden={gruppe.first.channel === 'web'}
                  beendenFuer={beendenFuer}
                  verwendenOffen={verwendenFuer === gruppe.contactPointId}
                  neuerZweck={verwendenFuer === gruppe.contactPointId ? neuerZweck : ''}
                  grund={grund}
                  onBeendenOeffnen={(contact) => {
                    // Ein an einer anderen Zeile gewaehlter Grund gehoert nicht zu dieser.
                    setGrund(OFFERED_END_REASONS[0]?.code ?? '');
                    verwendenSchliessen();
                    setKorrigierenFuer(undefined);
                    setBeendenFuer(contact.contactPointUsageId);
                  }}
                  korrektur={gruppe.first.channel === 'web' ? undefined : (
                    <KontaktwegKorrigieren
                      channel={gruppe.first.channel}
                      value={gruppe.value}
                      zwecke={gruppe.usages.map((usage) => usageLabel(usage.usageCode))}
                      sperrHinweise={sperrHinweise}
                      aufhebenAngeboten={haekchen}
                      offen={korrigierenFuer === gruppe.contactPointId}
                      busy={busy}
                      zuruecktreten={absendenFehlgeschlagen}
                      feldfehler={korrigierenFuer === gruppe.contactPointId ? feldfehler : []}
                      onOeffnen={() => {
                        setBeendenFuer(undefined);
                        verwendenSchliessen();
                        abschnitt.clearOutcome();
                        setSperrWarnung(undefined);
                        // Sonst stuenden "Hinzufügen" und "Korrektur speichern" als zwei Hauptaktionen da.
                        anlegen.erledigt();
                        setKorrigierenFuer(gruppe.contactPointId);
                      }}
                      onAbbrechen={() => setKorrigierenFuer(undefined)}
                      onSpeichern={(eingabe, sperreAufheben) => korrigieren(gruppe, eingabe, unzustellbar, sperreAufheben)}
                    />
                  )}
                  onBeendenAbbrechen={() => setBeendenFuer(undefined)}
                  onGrund={setGrund}
                  onBeenden={(contact) =>
                    befehl('end', (token) =>
                      endContactPoint({
                        baseUrl: apiBaseUrl,
                        accessToken: token,
                        partyId,
                        contactPointUsageId: contact.contactPointUsageId,
                        endReasonCode: grund,
                        lockVersion: contact.lockVersion,
                      }),
                    )
                  }
                  onBevorzugen={(contact) =>
                    befehl('prefer', (token) =>
                      preferContactPoint({
                        baseUrl: apiBaseUrl,
                        accessToken: token,
                        partyId,
                        contactPointUsageId: contact.contactPointUsageId,
                        lockVersion: contact.lockVersion,
                      }),
                    )
                  }
                  onNeuerZweck={setNeuerZweck}
                  onVerwendenOeffnen={() => {
                    setBeendenFuer(undefined);
                    setKorrigierenFuer(undefined);
                    setNeuerZweck('');
                    setVerwendenFuer(gruppe.contactPointId);
                  }}
                  onVerwendenAbbrechen={verwendenSchliessen}
                  onVerwenden={() => verwenden(gruppe, neuerZweck)}
                />
                );
              })}
            </Pflegeliste>

            <Pflegehistorie
              titel="Frühere Kontaktwege"
              anzahl={frueher.length}
              leer="Bisher gibt es keine früheren Kontaktwege."
              abgeschnitten={party.endedContactPointsTruncated ? HISTORY_TRUNCATED_NOTICE : undefined}
            >
              {frueher.map((entry) => (
                <FruehererKontaktweg key={entry.contactPointUsageId} entry={entry} label={entry.label} />
              ))}
            </Pflegehistorie>
          </>
        );
      }}
    </Pflegeabschnitt>
    </>
  );
}
